import { type PatchBuildingRoomsRequestBody } from "@eisox/api/core-api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { roomApi } from "~/api";
import { buildGroupsUpdateRequestBody, Groups, type Group } from "~/features/settings";
import { useBuildingContext } from "~/providers/building.provider";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/settings/rooms/groups")({
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();

  const { building, floors, rooms, roomsGroups } = useBuildingContext(Route.id);

  const { mutate } = useMutation({
    mutationFn: (body: PatchBuildingRoomsRequestBody) => roomApi.update(building.id, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: roomApi.queries.building(building.id).queryKey });
    },
  });

  const groups = roomsGroups.map(g => ({ name: g.name, items: g.rooms }));

  const handleGroupsChange = async (updatedGroups: Group[]): Promise<void> => {
    const groupsUpdate = buildGroupsUpdateRequestBody(updatedGroups, groups);
    const body: PatchBuildingRoomsRequestBody = groupsUpdate.map(g => ({
      ids: g.itemIds,
      groups: g.groupNames,
    }));
    return new Promise((resolve, reject) => {
      mutate(body, {
        onSuccess: () => resolve(),
        onError: error => reject(error),
      });
    });
  };

  return (
    <div className="h-full" data-testid="RoomsGroupsSettings">
      <Groups
        buildingId={building.id}
        floors={floors}
        rooms={rooms}
        groups={groups}
        onGroupsChange={handleGroupsChange}
      />
    </div>
  );
}
